<template>
    <div class="width-full-div">
        <div class="d-flex flex-nowrap align-center justify-center">
            <v-autocomplete :label="label" v-model="item" :items="computedItems" @input="onSelect" :search-input.sync="locationSearchText" item-text="description" item-value="place_id" hide-no-data return-object :key="autocompleteKey" :dense="dense" :readonly="readonly" />
            <v-btn v-if="!isExpanded && !readonly" text small fab class="ml-2" @click="isExpanded = true"><v-icon>mdi-chevron-down</v-icon></v-btn>
            <v-btn v-if="isExpanded && !readonly" text small fab class="ml-2" @click="isExpanded = false"><v-icon>mdi-chevron-up</v-icon></v-btn>
            <slot />
        </div>
        <v-expand-transition>
            <v-container v-show="isExpanded" class="inputs-container">
                <v-row>
                    <v-col>
                        <v-text-field label="Street" v-model="valueLocal.street" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field label="City" v-model="valueLocal.city" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field label="State" v-model="valueLocal.state" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <autocomplete v-model="valueLocal.countryId" @add="onCountrySelected" label="Country" item-text="label" item-value="id" :url="'/api/Countries/Autocomplete'" />
                    </v-col>
                    <v-col cols="4">
                        <v-text-field label="Postcode" v-model="valueLocal.postcode" />
                    </v-col>
                </v-row>
            </v-container>
        </v-expand-transition>
    </div>
</template>

<script>
    const google = window.google;
    import axios from 'axios';

    export default {
        props: [
            'label',
            'value',
            'rules',
            'dense',
            'readonly'
        ],
        components: {
            autocomplete: () => import('../components/Autocomplete')
        },
        data() {
            return {
                item: null,
                locationSearchText: null,
                items: [],
                valueLocal: {},
                isExpanded: false,
                autocompleteKey: 0,
                searchKey: 0,
                addressMappers: [
                    {
                        type: "number",
                        items: [
                            "room",
                            "floor",
                            "street_number",
                        ]
                    },
                    {
                        type: "street",
                        items: [
                            "street_address",
                            "route",
                            "plus_code",
                            "intersection",
                        ]
                    },
                    {
                        type: "suburb",
                        items: [
                            "sublocality",
                            "sublocality_level_1",
                            "sublocality_level_2",
                            "sublocality_level_3",
                            "sublocality_level_4",
                            "neighborhood",
                            "town_square",
                            "archipelago",
                            "colloquial_area",
                            "establishment",
                            "finance",
                            "food",
                            "general_contractor",
                            "health",
                            "landmark",
                            "natural_feature",
                            "place_of_worship",
                            "point_of_interest",
                            "post_box",
                        ]
                    },
                    {
                        type: "city",
                        items: [
                            "locality",
                        ]
                    },
                    {
                        type: "state",
                        items: [
                            "administrative_area_level_1",
                            "administrative_area_level_2",
                            "administrative_area_level_3",
                            "administrative_area_level_4",
                            "administrative_area_level_5",
                            "postal_town",
                        ]
                    },
                    {
                        type: "country",
                        items: [
                            "country",
                            "continent"
                        ]
                    },
                    {
                        type: "postcode",
                        items: [
                            "postal_code_prefix",
                            "postal_code",
                            "postal_code_suffix"
                        ]
                    }
                ],
            }
        },
        methods: {
            async getSuggestions(search) {
                var promise = await new Promise((resolve, reject) => {
                    var displaySuggestions = (predictions, status) => {
                        if (status !== google.maps.places.PlacesServiceStatus.OK) {
                            reject(status);
                        }
                        resolve(predictions);
                    }
                    var request = {
                        input: search,
                        location: new google.maps.LatLng(-23.700552, 133.882675),
                        radius: 2000000,
                        types: ['geocode']
                    }

                    var service = new google.maps.places.AutocompleteService();
                    service.getPlacePredictions(request, displaySuggestions);
                });

                return promise;
            },
            async onSelect(item) {
                var result = await this.getPlaceDetails(item.place_id);
                var latitude = result.geometry.location.lat();
                var longitude = result.geometry.location.lng();

                //Here we aim to parse the google maps address api into our database address state
                var parsedAddress = [
                    { type: "number", value: "" },
                    { type: "street", value: "" },
                    { type: "suburb", value: "" },
                    { type: "city", value: "" },
                    { type: "state", value: "" },
                    { type: "country", value: "" },
                    { type: "postcode", value: "" }
                ];
                var addressComponents = result.address_components;
                //Go backwards through the address components because google sorts them by seniority,
                //and we want to add the most important data and ignore excess information in the same category
                for (var i = addressComponents.length - 1; i > -1; i--) {
                    for (var j = this.addressMappers.length - 1; j > -1; j--) {
                        //If our address already has a value for one of its fields, don't add unnecessary excess data to it unless it's the house number or postcode
                        if (parsedAddress[j].value !== "" && parsedAddress[j].type !== "number" && parsedAddress[j] !== "postcode") {
                            continue;
                        }
                        for (var k = 0; k < this.addressMappers[j].items.length; k++) {
                            //Prioritise address component types in the order set in the addressMappers schema, throwing out excess fields that google gives us
                            if (addressComponents[i].types[0] === this.addressMappers[j].items[k] && (parsedAddress[j].value === "" || parsedAddress[j].type === "number" || parsedAddress[j] === "postcode")) {
                                parsedAddress[j].value += (parsedAddress[j].value === "" ? "" : " ") + (parsedAddress[j].type === "state" ? addressComponents[i].short_name : addressComponents[i].long_name);
                            }
                        }
                    }
                }

                var address = {
                    street: "",
                    city: "",
                    state: "",
                    postcode: ""
                };
                for (var a = 0; a < parsedAddress.length; a++) {
                    if (parsedAddress[a].type === "country") {
                        continue;
                    }
                    if (parsedAddress[a].type === "number") {
                        address.street += parsedAddress[a].value;
                    }
                    else if (parsedAddress[a].type == "street" && parsedAddress[a].value !== "") {
                        address.street += (address.street !== "" ? " " : "") + parsedAddress[a].value;
                    }
                    else if (parsedAddress[a].type == "suburb" && parsedAddress[a].value !== "") {
                        address.street += (address.street !== "" ? ", " : "") + parsedAddress[a].value;
                    }
                    else {
                        address[parsedAddress[a].type] = parsedAddress[a].value;
                    }
                }
                address.coordinates = { latitude, longitude };

                var countryName = parsedAddress.filter(i => i.type === "country")[0].value;
                var params = {};
                params['filter.name'] = countryName;
                var response = await axios.get('/api/Countries/', { params });
                var countries = response.data.items;
                if (countries.length !== 1) {
                    this.$store.dispatch('alert/error', "The address's country is not recognised");
                    return;
                }
                address.countryId = countries[0].id;
                address.country = countries[0].name;

                this.$emit('input', address);
            },
            async getPlaceDetails(placeId) {
                var promise = await new Promise((resolve, reject) => {
                    var callback = (place, status) => {
                        if (status !== google.maps.places.PlacesServiceStatus.OK) {
                            reject(status);
                        }
                        resolve(place);
                    }
                    var request = {
                        placeId: placeId
                    };

                    var service = new google.maps.places.PlacesService(document.createElement('div'));
                    service.getDetails(request, callback);
                })

                return promise;
            },
            isEmpty(value) {
                return typeof (value) === "undefined" || value === null || value === "";
            },
            onCountrySelected(item) {
                if (typeof item !== "undefined" && item !== null) {
                    this.valueLocal.country = item.label;
                }
                else {
                    this.valueLocal.country = "";
                }
            }
        },
        computed: {
            computedItems() {
                return this.items;
            },
        },
        watch: {
            valueLocal: {
                deep: true,
                handler() {
                    this.$emit('input', this.valueLocal);
                }
            },
            async locationSearchText(search) {
                if (!search) { return; }

                if (this.searchKey !== this.autocompleteKey) {
                    this.searchKey = this.autocompleteKey;
                    return;
                }
                this.items = await this.getSuggestions(search);
            },
            value: {
                deep: true,
                immediate: true,
                handler() {
                    if (this.value === null || typeof (this.value) === "undefined") {
                        return;
                    }
                    this.valueLocal = this.value;

                    var addressSections = [
                        this.value.street,
                        this.value.city,
                        this.value.state,
                        this.value.country
                    ];

                    var description = addressSections.filter((sec) => {
                        return sec != null
                    }).join(", ");

                    if (description === "") {
                        this.item = null;
                        this.items = [];
                        this.autocompleteKey++;
                    }
                    else if (this.item !== null && typeof (this.item) !== "undefined") {
                        var index = this.items.map(i => i.place_id).indexOf(this.item.place_id);
                        this.$set(this.item, "description", description);
                        this.$set(this.items[index], "description", description);
                        this.autocompleteKey++;
                    }
                    else {
                        var item = { description, place_id: description };
                        this.items.push(item);
                        this.item = item;
                        this.autocompleteKey++;
                    }
                }
            }
        }
    };
</script>
<style>
    .inputs-container {
        margin: -15px 0px -15px 0px;
    }

        .inputs-container .col {
            padding-top: 0;
            padding-bottom: 0;
        }

        .inputs-container .theme--light.v-input {
            padding-top: 0;
        }

        .inputs-container .v-input__slot {
            margin-bottom: 0;
        }
</style>
